import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Edit3,
  Trash2,
  Search,
  MapPin,
  Star,
  Home,
  Info,
  X,
} from "lucide-react";
import AddressFormModal from "./AddressFormModal";
import addressService from "@/services/addressService";
import { showInfo, showWarning } from "@/store/errorStore";

/**
 * Gerenciamento de endereços da empresa.
 * O AddressDTO da API não tem vínculo com o cliente: um endereço criado aqui
 * ficaria solto. Por isso esta tela só lista, edita, exclui e define o
 * principal; endereços novos são cadastrados pela tela de Clientes.
 * Erros HTTP já abrem o modal global (axiosInterceptor).
 */
export default function AddressesManagement() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientEmail, setClientEmail] = useState("");
  const [clientFilter, setClientFilter] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Carregar endereços (todos da empresa ou de um cliente pelo e-mail)
   */
  const loadData = async (email = clientFilter) => {
    setLoading(true);
    try {
      const data = email
        ? await addressService.getByClientEmail(email)
        : await addressService.getAllAddresses();
      setAddresses(Array.isArray(data) ? data : []);
    } catch (error) {
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = () => loadData();

  const handleFilterByClient = (e) => {
    e.preventDefault();
    const email = clientEmail.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showWarning("Informe um e-mail válido", "E-mail inválido");
      return;
    }
    setClientFilter(email);
    loadData(email);
  };

  const handleClearClientFilter = () => {
    setClientEmail("");
    setClientFilter("");
    loadData("");
  };

  /**
   * Abrir modal para editar endereço
   */
  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setShowModal(true);
  };

  /**
   * Salvar endereço (criar ou atualizar)
   */
  const handleSaveAddress = async (addressData) => {
    setSavingAddress(true);
    try {
      if (!editingAddress) return;
      await addressService.updateAddress(editingAddress.id, addressData);
      showInfo("Endereço atualizado com sucesso", "Endereço Atualizado");

      setShowModal(false);
      setEditingAddress(null);
      await fetchAddresses();
    } catch (error) {
      // Modal global já exibido pelo interceptor
    } finally {
      setSavingAddress(false);
    }
  };

  /**
   * Definir endereço como principal (PATCH /addresses/{id}/principal)
   */
  const handleSetAsPrincipal = async (address) => {
    try {
      await addressService.setPrincipal(address.id);
      showInfo(
        `Endereço "${address.logradouro || "-"}, ${address.number || "s/n"}" definido como principal`,
        "Endereço Principal"
      );
      await fetchAddresses();
    } catch (error) {
      // Modal global já exibido pelo interceptor
    }
  };

  /**
   * Confirmar exclusão de endereço
   */
  const handleDeleteClick = (address) => {
    setDeletingAddressId(address.id);
    setShowDeleteDialog(true);
  };

  /**
   * Executar exclusão de endereço
   */
  const handleConfirmDelete = async () => {
    if (!deletingAddressId) return;

    try {
      await addressService.deleteAddress(deletingAddressId);
      showInfo("Endereço removido com sucesso", "Endereço Deletado");
      await fetchAddresses();
    } catch (error) {
      // Modal global já exibido pelo interceptor
    } finally {
      setShowDeleteDialog(false);
      setDeletingAddressId(null);
    }
  };

  /**
   * Filtrar endereços por busca
   */
  const filteredAddresses = addresses.filter((address) => {
    const query = searchQuery.toLowerCase();
    const logradouro = (address.logradouro || "").toLowerCase();
    const localidade = (address.localidade || "").toLowerCase();
    const bairro = (address.bairro || "").toLowerCase();
    const uf = (address.uf || "").toLowerCase();
    const cep = (address.cep || "").toLowerCase();
    const digits = query.replace(/\D/g, "");

    return (
      logradouro.includes(query) ||
      localidade.includes(query) ||
      bairro.includes(query) ||
      uf.includes(query) ||
      cep.includes(query) ||
      (digits.length > 0 && cep.includes(digits))
    );
  });

  /**
   * Formatar endereço completo em uma linha
   */
  const formatFullAddress = (address) => {
    const parts = [
      address.logradouro,
      address.complemento,
      address.bairro,
      address.localidade,
      address.estado,
      address.uf,
    ].filter(Boolean);

    return parts.join(", ");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gerenciamento de Endereços
          </h1>
          <p className="text-gray-500 mt-1">
            {filteredAddresses.length}{" "}
            {filteredAddresses.length === 1 ? "endereço" : "endereços"}{" "}
            cadastrados
          </p>
        </div>

      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          Endereços novos são cadastrados junto com o cliente, na tela de
          Clientes. Aqui você edita, exclui e define o endereço principal.
        </span>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Buscar Endereços</CardTitle>
          <CardDescription>
            Pesquise por rua, cidade, bairro ou CEP, ou filtre pelo e-mail do
            cliente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <form
            onSubmit={handleFilterByClient}
            className="flex flex-col gap-2 sm:flex-row"
          >
            <Input
              type="email"
              placeholder="E-mail do cliente"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
            />
            <div className="flex gap-2">
              <Button type="submit" variant="outline">
                Filtrar
              </Button>
              {clientFilter && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClearClientFilter}
                  title="Limpar filtro"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </form>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Digite para buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Addresses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Endereços</CardTitle>
          <CardDescription>
            Gerencie os endereços cadastrados no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando endereços...</p>
              </div>
            </div>
          ) : filteredAddresses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MapPin className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">
                {searchQuery
                  ? "Nenhum endereço encontrado"
                  : "Nenhum endereço cadastrado"}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {searchQuery
                  ? "Tente outro termo de busca"
                  : clientFilter
                    ? "Este cliente não tem endereços cadastrados"
                    : "Cadastre endereços pela tela de Clientes"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Endereço</TableHead>
                    <TableHead>Bairro</TableHead>
                    <TableHead>Cidade/UF</TableHead>
                    <TableHead>CEP</TableHead>
                    <TableHead>Coordenadas</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAddresses.map((address) => (
                    <TableRow key={address.id}>
                      <TableCell className="font-mono text-sm text-gray-500">
                        #{address.id}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div
                          className="truncate"
                          title={formatFullAddress(address)}
                        >
                          {address.logradouro || "-"}
                          {address.complemento && ` - ${address.complemento}`}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {address.bairro || "-"}
                      </TableCell>
                      <TableCell>
                        {address.localidade || "-"}/{address.uf || "-"}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {address.cep
                          ? address.cep.replace(/(\d{5})(\d{3})/, "$1-$2")
                          : "-"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {address.latitude && address.longitude ? (
                          <div>
                            <div>Lat: {Number(address.latitude).toFixed(6)}</div>
                            <div>Lng: {Number(address.longitude).toFixed(6)}</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {address.principal ? (
                          <Badge
                            variant="outline"
                            className="bg-green-100 text-green-800 border-green-200"
                          >
                            <Star className="w-3 h-3 mr-1 fill-current" />
                            Principal
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-600">
                            <Home className="w-3 h-3 mr-1" />
                            Secundário
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {!address.principal && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetAsPrincipal(address)}
                              title="Definir como principal"
                            >
                              <Star className="w-4 h-4 mr-1" />
                              Principal
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditAddress(address)}
                          >
                            <Edit3 className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteClick(address)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Address Form Modal */}
      <AddressFormModal
        open={showModal}
        onOpenChange={setShowModal}
        address={editingAddress}
        onSave={handleSaveAddress}
        loading={savingAddress}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este endereço? Esta ação não pode
              ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingAddressId(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
